import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import { FaList, FaMapMarked } from "react-icons/fa";
import { SearchResultsFormatType } from "../useSearchListingsPage";
import MyLocationToolbarButton from "../../common/maps/MyLocationToolbarButton";
import FeatureDiscoveryTooltip from "../../FeatureDiscoveryTooltip";
import { Listing } from "src/types/listing";
import Pagination from "../Pagination";

interface CreateSearchResultsFormatSelectorParams {
  suppressMyLocation?: boolean;
  isShowMyLocationDiscoveryTooltip?: boolean;
  isShowSearchResultsFormatTypeDiscoveryTooltip?: boolean;
}

interface CreateSearchResultsFormatSelectorProps {
  searchResultsFormat: SearchResultsFormatType;
  setSearchResultsFormat: (format: SearchResultsFormatType) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setMapCenter?: (center: any) => void;
  selectedPageListing: Listing;
  scrollToItemWithId?: (id: string) => void;
  onSelectListing?: (listing: Listing) => void;
  listings: Listing[];
  currentFeature?: string;
  nextFeature: () => void;
  isLastFeature: boolean;
}

interface CreatePaginationSelectorConfig {
  suppressPageSelector?: boolean;
  suppressNavigationButtons?: boolean;
}

interface CreatePaginationSelectorProps {
  totalRecords: number;
  pageSize: number;
  currentPage: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSearchParamsUpdated: (params: any) => void;
}


export const createSearchResultsFormatSelector = (
  {
    suppressMyLocation = false,
    isShowMyLocationDiscoveryTooltip = false,
    isShowSearchResultsFormatTypeDiscoveryTooltip = false,
  }: CreateSearchResultsFormatSelectorParams = {
    suppressMyLocation: false,
    isShowMyLocationDiscoveryTooltip: false,
    isShowSearchResultsFormatTypeDiscoveryTooltip: false,
  }
) => {
  return ({
    searchResultsFormat,
    setSearchResultsFormat,
    setMapCenter,
    selectedPageListing,
    scrollToItemWithId,
    onSelectListing,
    listings,
    currentFeature,
    nextFeature,
    isLastFeature,
  }: CreateSearchResultsFormatSelectorProps) => {
    const myLocationButton = (
      <MyLocationToolbarButton
        onClick={async (currentUserGeoLocation) => {
          if (currentUserGeoLocation) {
            setMapCenter?.(currentUserGeoLocation);
            setSearchResultsFormat(SearchResultsFormatType.map);
          }
        }}
      />
    );

    const searchResultsFormatButtonGroup = (
      <ToggleButtonGroup
        value={searchResultsFormat}
        exclusive
        onChange={(event, newSearchResultsFormat) => {
          if (!newSearchResultsFormat) {
            return;
          }

          if (
            newSearchResultsFormat === SearchResultsFormatType.list &&
            selectedPageListing
          ) {
            setTimeout(() => {
              // When the user returns to list view, scroll to the current listing
              scrollToItemWithId?.(selectedPageListing.id);
            }, 100);
          }

          // If the user toggles to map and there is no current listing make the first
          // listing be the selected on the map
          if (
            newSearchResultsFormat === SearchResultsFormatType.map &&
            !selectedPageListing
          ) {
            onSelectListing?.(listings?.[0]);
          }

          setSearchResultsFormat(newSearchResultsFormat);
        }}
        aria-label="text alignment"
      >
        <ToggleButton
          value={SearchResultsFormatType.list}
          aria-label={SearchResultsFormatType.list}
        >
          <FaList />
        </ToggleButton>
        <ToggleButton
          value={SearchResultsFormatType.map}
          aria-label={SearchResultsFormatType.map}
        >
          <FaMapMarked />
        </ToggleButton>
      </ToggleButtonGroup>
    );

    return (
      <div className="flex gap-3">
        {!suppressMyLocation && isShowMyLocationDiscoveryTooltip && (
          <FeatureDiscoveryTooltip
            content="Click to move the map to your location and highlight nearby listings returned in your search"
            isOpen
            onNext={nextFeature}
            isLast={isLastFeature}
          >
            {myLocationButton}
          </FeatureDiscoveryTooltip>
        )}
        {!suppressMyLocation &&
          !isShowMyLocationDiscoveryTooltip &&
          myLocationButton}
        {isShowSearchResultsFormatTypeDiscoveryTooltip && (
          <FeatureDiscoveryTooltip
            content="Use these buttons to switch between list and map views."
            isOpen
            onNext={nextFeature}
            isLast={isLastFeature}
          >
            <div id="search-results-format">
              {searchResultsFormatButtonGroup}
            </div>
          </FeatureDiscoveryTooltip>
        )}
        {!isShowSearchResultsFormatTypeDiscoveryTooltip &&
          searchResultsFormatButtonGroup}
      </div>
    );
  };
};

export const createPaginationSelector = (
  {
    suppressPageSelector = false,
    suppressNavigationButtons = false,
  }: CreatePaginationSelectorConfig = {
    suppressPageSelector: false,
    suppressNavigationButtons: false,
  }
) =>
  ({ totalRecords, pageSize, currentPage, onSearchParamsUpdated }: CreatePaginationSelectorProps) => (
    <Pagination
      numRecords={totalRecords}
      pageSize={pageSize}
      currentPage={currentPage}
      suppressPageSelector={suppressPageSelector}
      suppressNavigationButtons={suppressNavigationButtons}
      onPageChange={(updatedPage: number) => {
        onSearchParamsUpdated({ currentPage: updatedPage });
      }}
    />
  );

